import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MembershipV2Service } from 'src/app/services/membership-v2.service';

interface ImportRow {
  line: number;          // row number in the sheet (header = 1)
  name: string;
  email: string;
  status: 'ready' | 'skip' | 'importing' | 'added' | 'failed';
  note: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_HEADERS = ['name', 'navn', 'full name', 'fullt navn'];
const EMAIL_HEADERS = ['email', 'e-mail', 'e-post', 'epost', 'mail'];

// Bulk add employees from an Excel/CSV sheet with two columns, Name and Email.
// Every row goes through the same addEmployeeMembership call as "Add employee",
// one at a time, so the server's duplicate check and welcome email behave the
// same. Rows already at this site, repeated in the sheet or without a valid
// email are shown and skipped before anything is sent.
@Component({
  selector: 'v2-import-employees-dialog',
  templateUrl: './import-employees-dialog.component.html',
  styleUrls: ['./dialog-shared.css']
})
export class ImportEmployeesDialogComponent {
  groupId: string = null;
  sendInvite = true;
  fileName = '';
  fileError = '';
  rows: ImportRow[] = [];
  running = false;
  finished = false;
  done = 0;
  total = 0;

  constructor(
    public ref: MatDialogRef<ImportEmployeesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { siteId: string; groups: any[]; existingEmails: string[] },
    private svc: MembershipV2Service
  ) { }

  get ready() { return this.rows.filter(r => r.status === 'ready').length; }
  get skipped() { return this.rows.filter(r => r.status === 'skip').length; }
  get added() { return this.rows.filter(r => r.status === 'added').length; }
  get failed() { return this.rows.filter(r => r.status === 'failed').length; }

  async downloadTemplate() {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([['Name', 'Email']]);
    ws['!cols'] = [{ wch: 30 }, { wch: 36 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, 'employees-import-template.xlsx');
  }

  async onFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';               // picking the same file again re-reads it
    if (!file) { return; }
    this.fileName = file.name;
    this.fileError = '';
    this.rows = [];
    this.finished = false;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const table: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
      this.rows = this.parse(table);
      if (!this.rows.length) { this.fileError = 'No employees found in the file.'; }
    } catch (e) {
      this.fileError = e && e.message ? e.message : 'Could not read the file.';
    }
  }

  private parse(table: any[][]): ImportRow[] {
    const headerIdx = table.findIndex(r => r.some(c => String(c).trim() !== ''));
    if (headerIdx < 0) { return []; }
    const header = table[headerIdx].map(c => String(c).trim().toLowerCase());
    const nameCol = header.findIndex(h => NAME_HEADERS.includes(h));
    const emailCol = header.findIndex(h => EMAIL_HEADERS.includes(h));
    if (emailCol < 0) { throw new Error('The file needs an "Email" column. Download the template to see the format.'); }

    const existing = new Set((this.data.existingEmails || []).map(e => String(e).trim().toLowerCase()));
    const seen = new Set<string>();
    const rows: ImportRow[] = [];
    for (let i = headerIdx + 1; i < table.length; i++) {
      const r = table[i] || [];
      const name = nameCol >= 0 ? String(r[nameCol] || '').trim() : '';
      const email = String(r[emailCol] || '').trim().toLowerCase();
      if (!name && !email) { continue; }
      const row: ImportRow = { line: i + 1, name, email, status: 'ready', note: '' };
      if (!email) { row.status = 'skip'; row.note = 'No email'; }
      else if (!EMAIL_RE.test(email)) { row.status = 'skip'; row.note = 'Not a valid email'; }
      else if (seen.has(email)) { row.status = 'skip'; row.note = 'Already earlier in the file'; }
      else if (existing.has(email)) { row.status = 'skip'; row.note = 'Already registered at this site'; }
      seen.add(email);
      rows.push(row);
    }
    return rows;
  }

  async import() {
    if (this.running || !this.ready || !this.groupId) { return; }
    this.running = true;
    this.done = 0;
    this.total = this.ready;
    for (const row of this.rows.filter(r => r.status === 'ready')) {
      row.status = 'importing';
      const result = await this.svc.addEmployee(this.data.siteId, {
        name: row.name,
        email: row.email,
        employeeId: '',
        password: '',
        groupId: this.groupId,
        isAdmin: false,
        sendInvite: this.sendInvite
      }, false, false);
      if (result && result.success) {
        row.status = 'added';
        row.note = this.sendInvite && !result.invited ? 'Added, welcome email not sent' : (this.sendInvite ? 'Added, welcome email sent' : 'Added');
      } else {
        row.status = 'failed';
        row.note = this.svc.addEmployeeError(result);
      }
      this.done++;
    }
    this.running = false;
    this.finished = true;
  }

  close() { this.ref.close(this.added > 0); }
}
